import { logger } from '@fiverr-private/obs';
import type {
    BriefGeneratedPayload,
    ErrorPayload,
    MaxInvitesReachedPayload,
    FreelancerInvitedPayload,
    UpgradeToProPayload,
    BriefCreatedPayload,
    BriefDeletedSuccessfullyPayload,
    BriefActionPayload,
} from './types';

/**
 * Extend Window interface to include mobile bridge interfaces
 */

function isAndroidApp(): boolean {
    return typeof window.AndroidBriefInterface !== 'undefined';
}

function isIOSApp(): boolean {
    return (
        typeof window.webkit !== 'undefined' &&
        typeof window.webkit.messageHandlers !== 'undefined' &&
        typeof window.webkit.messageHandlers.onBriefCreationDone !== 'undefined' &&
        typeof window.webkit.messageHandlers.onBriefGenerated !== 'undefined' &&
        typeof window.webkit.messageHandlers.onError !== 'undefined' &&
        typeof window.webkit.messageHandlers.onMaxInvitesReached !== 'undefined' &&
        typeof window.webkit.messageHandlers.onFreelancerInvited !== 'undefined' &&
        typeof window.webkit.messageHandlers.onUpgradeToPro !== 'undefined' &&
        typeof window.webkit.messageHandlers.onOpenBriefLobby !== 'undefined' &&
        typeof window.webkit.messageHandlers.onBriefDeletedSuccessfully !== 'undefined' &&
        typeof window.webkit.messageHandlers.onBriefAction !== 'undefined'
    );
}

function isMobileApp(): boolean {
    return isAndroidApp() || isIOSApp();
}

interface HandlerResult {
    dispatched: boolean;
}

/**
 * Mobile Bridge Utility Class
 * Provides methods to emit events to mobile app webview
 */
class BriefsMobileBridge {
    static onBriefGenerated(payload: BriefGeneratedPayload): void {
        try {
            if (isAndroidApp()) {
                window.AndroidBriefInterface?.onBriefGenerated(payload.userPrompt);
            } else if (isIOSApp()) {
                window.webkit?.messageHandlers?.onBriefGenerated?.postMessage({ userPrompt: payload.userPrompt });
            }
        } catch (error) {
            logger.warn('Failed to send brief generated event', { payload, error });
        }
    }

    static onBriefCreationDone(payload: BriefCreatedPayload): void {
        try {
            if (isAndroidApp()) {
                window.AndroidBriefInterface?.onBriefCreationDone(payload.briefId, payload.flow);
            } else if (isIOSApp()) {
                window.webkit?.messageHandlers?.onBriefCreationDone?.postMessage({
                    briefId: payload.briefId,
                    flow: payload.flow,
                });
            }
        } catch (error) {
            logger.warn('Failed to send brief creation done event', { payload, error });
        }
    }

    static onError(payload: ErrorPayload): void {
        try {
            if (isAndroidApp()) {
                window.AndroidBriefInterface?.onError(payload.type);
            } else if (isIOSApp()) {
                window.webkit?.messageHandlers?.onError?.postMessage({ type: payload.type });
            }
        } catch (error) {
            logger.warn('Failed to send error event', { payload, error });
        }
    }

    static onMaxInvitesReached(payload: MaxInvitesReachedPayload): void {
        try {
            if (isAndroidApp()) {
                window.AndroidBriefInterface?.onMaxInvitesReached(payload.invites);
            } else if (isIOSApp()) {
                window.webkit?.messageHandlers?.onMaxInvitesReached?.postMessage({ invites: payload.invites });
            }
        } catch (error) {
            logger.warn('Failed to send max invites reached event', { payload, error });
        }
    }

    static onFreelancerInvited(payload: FreelancerInvitedPayload): void {
        try {
            if (isAndroidApp()) {
                window.AndroidBriefInterface?.onFreelancerInvited(
                    payload.sellerId,
                    payload.navigationSource,
                    payload.briefId
                );
            } else if (isIOSApp()) {
                window.webkit?.messageHandlers?.onFreelancerInvited?.postMessage({
                    sellerId: payload.sellerId,
                    navigationSource: payload.navigationSource,
                    briefId: payload.briefId,
                });
            }
        } catch (error) {
            logger.warn('Failed to send freelancer invited event', { payload, error });
        }
    }

    static onUpgradeToPro(payload: UpgradeToProPayload): void {
        try {
            if (isAndroidApp()) {
                window.AndroidBriefInterface?.onUpgradeToPro(payload.buyerId);
            } else if (isIOSApp()) {
                window.webkit?.messageHandlers?.onUpgradeToPro?.postMessage({ buyerId: payload.buyerId });
            }
        } catch (error) {
            logger.warn('Failed to send upgrade to pro event', { payload, error });
        }
    }

    static onOpenBriefLobby(): void {
        try {
            if (isAndroidApp()) {
                window.AndroidBriefInterface?.onOpenBriefLobby();
            } else if (isIOSApp()) {
                window.webkit?.messageHandlers?.onOpenBriefLobby?.postMessage({});
            }
        } catch (error) {
            logger.warn('Failed to send open brief lobby event', { error });
        }
    }

    static onBriefDeletedSuccessfully(payload: BriefDeletedSuccessfullyPayload): HandlerResult {
        try {
            if (isAndroidApp()) {
                window.AndroidBriefInterface?.onBriefDeletedSuccessfully(payload.briefId);
                return { dispatched: true };
            } else if (isIOSApp()) {
                window.webkit?.messageHandlers?.onBriefDeletedSuccessfully?.postMessage({
                    briefId: payload.briefId,
                });
                return { dispatched: true };
            }
        } catch (error) {
            logger.warn('Failed to send brief deleted successfully event', { payload, error });
        }
        return { dispatched: false };
    }

    static onBriefAction(payload: BriefActionPayload): HandlerResult {
        try {
            if (isAndroidApp()) {
                window.AndroidBriefInterface?.onBriefAction(payload.action);
                return { dispatched: true };
            } else if (isIOSApp()) {
                window.webkit?.messageHandlers?.onBriefAction?.postMessage({ action: payload.action });
                return { dispatched: true };
            }
        } catch (error) {
            logger.warn('Failed to send brief action event', { payload, error });
        }
        return { dispatched: false };
    }

    static isAndroidApp = isAndroidApp;

    static isIOSApp = isIOSApp;

    static isMobileApp = isMobileApp;
}

export { BriefsMobileBridge, isAndroidApp, isIOSApp, isMobileApp };
